<template>
  <v-card no-body class="mb-3">
    <v-card-title class="customer-card-header">
      {{ customerReport.name }} -&nbsp;
      <span class="font-weight-normal">
        {{ formatHeaderDate(startDate) }}
        {{ $t('to') }}
        {{ formatHeaderDate(endDate) }}
      </span>
    </v-card-title>
    <v-card-text>
      <c3-chart :height="325" :data="chartData" :config="chartConfig" type='area' />
    </v-card-text>
  </v-card>
</template>
<script>
import Moment from 'moment-timezone'
import { map, round } from 'lodash'
import C3Chart from '@/components/shared/chart/C3Chart'
import MathUtil from '@/util/MathUtil'

export default {
  name: 'RoutePlanHistoryReportCard',
  components: {
    C3Chart
  },
  props: {
    customerReport: {
      type: Object
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    }
  },
  computed: {
    chartData () {
      const dateColumn = map(this.customerReport.data, x => x.date)
      dateColumn.unshift('date')

      // On days with no route plans we can't calculate a meaningful percentage, so we render a gap (null) rather than
      // 0%, which alarms users by looking like a performance dip. This mirrors the dashboard chart (RoutePlansChart).
      const linkedRoutePlansColumn = map(
        this.customerReport.data,
        x => {
          if (x.total_route_plans == null || x.total_route_plans === 0) {
            return null
          }
          return MathUtil.percentage(x.linked_route_plans, x.total_route_plans)
        }
      )
      linkedRoutePlansColumn.unshift(this.$tc('linked_route_plan', 2))

      const assignedRoutesColumn = map(
        this.customerReport.data,
        x => {
          if (x.num_total_routes == null || x.num_total_routes === 0) {
            return null
          }
          return MathUtil.percentage(x.num_assigned_routes, x.num_total_routes)
        }
      )
      assignedRoutesColumn.unshift(this.$tc('assigned_route', 2))

      // We pivoted from showing missed stops to completed stops. We're not storing completed stops data in the history,
      // but we can easily get it just by subtracting the percentage of missed stops from 100.
      const completedStopsColumn = map(
        this.customerReport.data,
        x => {
          // No data for the day: render a gap rather than a misleading 0%.
          if (x.planned_stops == null || x.planned_stops === 0) {
            return null
          }
          return 100 - MathUtil.percentage(x.missed_stops, x.planned_stops)
        }
      )
      completedStopsColumn.unshift(this.$tc('completed_stop', 2))

      return {
        x: 'date',
        columns: [
          dateColumn,
          linkedRoutePlansColumn,
          assignedRoutesColumn,
          completedStopsColumn
        ]
      }
    },
    /**
     * The chart config.
     */
    chartConfig () {
      return {
        axis: {
          x: {
            label: {
              text: this.$t('date')
            },
            type: 'timeseries',
            tick: {
              format: '%Y-%m-%d',
              centered: true,
              culling: true
            }
          },
          y: {
            label: {
              text: this.$t('percentage')
            },
            tick: {
              values: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
              format: function (d) {
                return round(d, 1) + '%'
              },
              culling: true
            },
            max: 100
          }
        },
        line: {
          connectNull: true
        }
      }
    }
  },
  methods: {
    formatHeaderDate (date) {
      // parseZone keeps the offset the report was generated with, so the header always matches the range that was
      // selected in the form (and queried on the back end) rather than shifting with the viewer's browser timezone.
      return Moment.parseZone(date).format('ll')
    }
  }
}
</script>

<style scoped>
.customer-card-header {
  font-weight: bold;
  font-size: 1.1rem;
}
</style>
